import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { registerRemoteApps, prefetchRemoteEntries } from '@/apps/_registry'
import { perf } from '@/lib/perf'
import type { ClientOrgParams } from '@/types/focus-shell'

// Re-export para compatibilidad con consumidores existentes que importan
// desde este módulo (`import { ClientOrgParams } from '@/hooks/useEntitlements'`).
export type { ClientOrgParams } from '@/types/focus-shell'

/**
 * Organización del usuario
 */
export interface Organization {
  id: string
  name: string
  code: string
  logoUrl?: string
  primaryColor?: string
}

/**
 * Perfil del usuario con rol y permisos
 */
export interface UserProfile {
  id: string
  email: string
  displayName: string
  role: 'admin' | 'trader' | 'viewer'
}

/**
 * App con datos de Module Federation del API
 */
export interface AppEntitlement {
  appId: string
  code: string
  name: string
  description: string
  icon: string
  version: string
  minFocusVersion: string
  config: Record<string, unknown>
  category?: string
  requiresAdmin?: boolean
  remoteUrl: string
  scope: string
  module: string
}

/**
 * Respuesta del API de entitlements
 *
 * `ClientOrgParams` (con `servidor_origen` incluido) vive en
 * `@/types/focus-shell` para ser shared entre el hook, el store
 * y el contrato de propagación a las apps remotas.
 */
interface EntitlementsResponse {
  organization: Organization
  userProfile: UserProfile
  availableApps: AppEntitlement[]
  enableUat?: boolean
  orgParams?: ClientOrgParams
}

/**
 * Resultado del hook useEntitlements
 */
export interface UseEntitlementsReturn {
  // Estado
  isLoading: boolean
  error: string | null
  isForbidden: boolean  // true cuando el usuario no tiene acceso a este deployment
  remoteAppsReady: boolean  // true when registerRemoteApps has been called

  // Datos
  organization: Organization | null
  userProfile: UserProfile | null
  availableApps: AppEntitlement[]
  enableUat: boolean
  orgParams: ClientOrgParams

  // Helpers
  hasApp: (appId: string) => boolean
  isAdmin: () => boolean
  getAppConfig: (appId: string) => Record<string, unknown> | null

  // Acciones
  refetch: () => Promise<void>
}

// --- sessionStorage cache for instant refresh ---
const CACHE_KEY_PREFIX = 'focus:entitlements:'
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

interface CachedEntitlements {
  data: EntitlementsResponse
  timestamp: number
}

function readCache(uid: string): EntitlementsResponse | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY_PREFIX + uid)
    if (!raw) return null
    const cached: CachedEntitlements = JSON.parse(raw)
    if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_KEY_PREFIX + uid)
      return null
    }
    return cached.data
  } catch {
    return null
  }
}

function writeCache(uid: string, data: EntitlementsResponse) {
  try {
    const entry: CachedEntitlements = { data, timestamp: Date.now() }
    sessionStorage.setItem(CACHE_KEY_PREFIX + uid, JSON.stringify(entry))
  } catch { /* sessionStorage full or unavailable */ }
}

function applyEntitlements(
  data: EntitlementsResponse,
  setters: {
    setOrganization: (v: Organization) => void
    setUserProfile: (v: UserProfile) => void
    setEnableUat: (v: boolean) => void
    setOrgParams: (v: ClientOrgParams) => void
    setAvailableApps: (v: AppEntitlement[]) => void
    setRemoteAppsReady: (v: boolean) => void
  }
) {
  const apps = data.availableApps || []
  setters.setOrganization(data.organization)
  setters.setUserProfile(data.userProfile)
  setters.setEnableUat(data.enableUat === true)
  setters.setOrgParams(data.orgParams || {})
  setters.setAvailableApps(apps)

  registerRemoteApps(apps.map(app => ({
    appId: app.appId,
    remoteUrl: app.remoteUrl,
    scope: app.scope,
    module: app.module,
  })))
  setters.setRemoteAppsReady(true)
}

export function useEntitlements(): UseEntitlementsReturn {
  // Only depends on user, not hasLicense — entitlements checks per-app licenses server-side
  const { user } = useAuth()

  const [organization, setOrganization] = useState<Organization | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [availableApps, setAvailableApps] = useState<AppEntitlement[]>([])
  const [enableUat, setEnableUat] = useState(false)
  const [orgParams, setOrgParams] = useState<ClientOrgParams>({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isForbidden, setIsForbidden] = useState(false)
  const [remoteAppsReady, setRemoteAppsReady] = useState(false)

  const hasFetched = useRef(false)
  const lastUserId = useRef<string | null>(null)
  const setters = useRef({ setOrganization, setUserProfile, setEnableUat, setOrgParams, setAvailableApps, setRemoteAppsReady })

  const fetchEntitlements = useCallback(async () => {
    if (!user) {
      setAvailableApps([])
      setOrganization(null)
      setUserProfile(null)
      setEnableUat(false)
      setOrgParams({})
      setError(null)
      setIsLoading(false)
      return
    }

    if (hasFetched.current && lastUserId.current === user.uid) {
      setIsLoading(false)
      return
    }

    setError(null)
    setIsForbidden(false)

    // --- Restore from cache for instant UI ---
    const cached = readCache(user.uid)
    if (cached) {
      const endCache = perf.span('focus:entitlements:cache-restore')
      applyEntitlements(cached, setters.current)
      endCache()
      setIsLoading(false)
      // Prefetch remoteEntry.js scripts while we refresh in background
      prefetchRemoteEntries((cached.availableApps || []).map(a => a.remoteUrl))
    } else {
      setIsLoading(true)
    }

    // --- Always fetch fresh data (background refresh if cached) ---
    try {
      const endEntTotal = perf.span('focus:entitlements:total')

      const endToken = perf.span('focus:entitlements:get-token')
      const token = await user.getIdToken()
      endToken()

      const endFetch = perf.span('focus:entitlements:api-fetch')
      const apiUrl = import.meta.env.VITE_FOCUS_API_URL || ''
      const response = await fetch(`${apiUrl}/entitlements`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      endFetch()

      if (!response.ok) {
        console.error('Entitlements API failed:', response.status)
        if (!cached) {
          setAvailableApps([])
          setOrganization(null)
          setUserProfile(null)
        }
        if (response.status === 403) {
          setIsForbidden(true)
          setError('User is not authorized for this deployment')
        } else {
          setError(`HTTP ${response.status}`)
        }
        return
      }

      const data: EntitlementsResponse = await response.json()

      const endRegister = perf.span('focus:entitlements:register-remote-apps')
      applyEntitlements(data, setters.current)
      endRegister()

      writeCache(user.uid, data)
      endEntTotal()

      hasFetched.current = true
      lastUserId.current = user.uid

    } catch (err) {
      console.error('Error fetching entitlements:', err)
      if (!cached) {
        setError(err instanceof Error ? err.message : 'Error desconocido')
        setAvailableApps([])
        setOrganization(null)
        setUserProfile(null)
        setEnableUat(false)
        setOrgParams({})
      }
    } finally {
      setIsLoading(false)
    }
  }, [user])

  useEffect(() => {
    if (user?.uid !== lastUserId.current) {
      hasFetched.current = false
    }
    fetchEntitlements()
  }, [fetchEntitlements, user?.uid])

  const hasApp = useCallback((appId: string): boolean => {
    return availableApps.some(app => app.appId === appId)
  }, [availableApps])

  const isAdmin = useCallback((): boolean => {
    return userProfile?.role === 'admin'
  }, [userProfile])

  const getAppConfig = useCallback((appId: string): Record<string, unknown> | null => {
    const app = availableApps.find(a => a.appId === appId)
    return app?.config ?? null
  }, [availableApps])

  const refetch = useCallback(async () => {
    hasFetched.current = false
    await fetchEntitlements()
  }, [fetchEntitlements])

  return {
    isLoading,
    error,
    isForbidden,
    remoteAppsReady,
    organization,
    userProfile,
    availableApps,
    enableUat,
    orgParams,
    hasApp,
    isAdmin,
    getAppConfig,
    refetch
  }
}
